/**
 * Search Loads Component - Unified API Version
 * Refactored to use the new unified API system
 * Maintains same functionality but uses ApiManager and UnifiedApiStatus
 */
import React, { useState, useEffect } from 'react';
import {
  Search, Filter, MapPin, Truck, DollarSign, Calendar, Package,
  Scale, Ruler, AlertCircle, Mail,
  BarChart3, Map, Eye, EyeOff, ArrowUpDown, ArrowUp, ArrowDown, Check, X,
  ChevronDown, ChevronUp, Settings, Layers, Navigation, MessageSquare,
  Maximize, Minimize
} from 'lucide-react';
import apiManager from '../services/api/ApiManager';
import UnifiedApiStatus from './common/UnifiedApiStatus';
// import ApiDebugger from './ApiDebugger'; // Removed API debugger
import BubbleMapResults from './BubbleMapResults';
import { useData } from '../contexts/DataContext';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth0 } from '@auth0/auth0-react';
import useSearchLogger from '../hooks/useSearchLogger';

// Constants from the original app (unchanged)
const EQUIPMENT_TYPES = ['AUTO', 'BEAM', 'CONG', 'CONT', 'CV', 'DD', 'DDE', 'F', 'FA', 'FEXT', 'FO', 'FSD', 'FSDV', 'FVR', 'HS', 'HTU', 'LAF', 'LB', 'LBO', 'LDOT', 'MAXI', 'PO', 'R', 'RGN', 'RGNE', 'ROLL', 'RV', 'SD', 'SDC', 'SDL', 'SDO', 'SDRG', 'SPEC', 'SPV', 'SV', 'TANK', 'V', 'VCAR', 'VF', 'VLG', 'VM', 'VR', 'VV'];
const DEFAULT_EQUIPMENT_TYPES = ['F', 'FA', 'FSD', 'SDL', 'CONG', 'HS'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const HAUL_TYPES = ['Short Haul', 'Medium Haul', 'Long Haul'];
const DEFAULT_HAUL_TYPES = ['Medium Haul', 'Long Haul'];
const LOAD_TYPES = ['Full', 'Partial'];
const DEFAULT_LOAD_TYPES = ['Full', 'Partial'];

// Equipment types for search bar dropdown (user-friendly names to codes)
const SEARCH_EQUIPMENT_OPTIONS = [
  { label: 'All Equipment', value: 'All' },
  { label: 'Flatbed', value: 'F' },
  { label: 'Van', value: 'V' },
  { label: 'Reefer', value: 'R' },
  { label: 'Hot Shot', value: 'HS' }
];

// Mapbox Geocoding API for city suggestions - use same token as map
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;

// State name to abbreviation mapping
const STATE_ABBREVIATIONS = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC', 'Puerto Rico': 'PR'
};

// Function to get city suggestions from Mapbox Geocoding API
const getMapboxCitySuggestions = async (query) => {
  if (!query || query.length < 2) return [];
  
  // Check if token is available
  if (!MAPBOX_ACCESS_TOKEN) {
    console.warn('🌍 Mapbox token not available, skipping city suggestions');
    return [];
  }
  
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=US&types=place&limit=10`;
    console.log('🌍 Mapbox API request:', url);
    
    const response = await fetch(url);
    
    console.log('🌍 Mapbox API response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('🌍 Mapbox API error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('🌍 Mapbox API data:', data);
    
    // Format suggestions as "City, State" with abbreviations
    const suggestions = data.features.map(feature => {
      const placeName = feature.place_name;
      console.log('🌍 Processing place:', placeName);
      const parts = placeName.split(', ');
      
      if (parts.length >= 2) {
        const city = parts[0];
        const stateFull = parts[1];
        
        // Convert full state name to abbreviation
        const stateAbbr = STATE_ABBREVIATIONS[stateFull] || stateFull;
        
        return `${city}, ${stateAbbr}`;
      }
      
      return placeName;
    });
    
    const filtered = suggestions.filter(suggestion => suggestion.includes(', '));
    console.log('🌍 Final suggestions:', filtered);
    
    return filtered;
    
  } catch (error) {
    console.error('🌍 Error fetching Mapbox city suggestions:', error);
    return [];
  }
};

function SearchLoadsUnified() {
  // User context for role-based limitations
  const { userRole, userData, user } = useUser();
  const { isDarkMode } = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  
  // Search logging hook
  const { logLoadSearch, logCityClick, logRouteGeneration, logTableSort } = useSearchLogger();
  
  // City suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const [searchFilters, setSearchFilters] = useState(() => {
    // Load saved origin and equipment from localStorage
    const savedOrigin = localStorage.getItem('lotwizard_search_origin');
    const savedEquipment = localStorage.getItem('lotwizard_search_equipment');
    return {
      origin: savedOrigin || 'Chicago, IL',
      pickupDate: new Date().toISOString().split('T')[0],
      equipment: savedEquipment || 'All', // Load saved equipment or default to 'All'
      originRange: 100
    };
  });

  const [filters, setFilters] = useState(() => {
    try {
      const savedFilters = localStorage.getItem('lotwizard_search_filters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        // Merge saved filters with defaults, excluding equipment types
        return {
          minPayment: parsed.minPayment || 500,
          minRatePerMile: parsed.minRatePerMile || 2.50,
          minMiles: parsed.minMiles || 0,
          maxLength: parsed.maxLength || 60,
          maxWeight: parsed.maxWeight || 50000,
          haulTypes: parsed.haulTypes || DEFAULT_HAUL_TYPES,
          loadTypes: parsed.loadTypes || DEFAULT_LOAD_TYPES,
          destinationStates: parsed.destinationStates || US_STATES,
          equipmentTypes: DEFAULT_EQUIPMENT_TYPES, // Always use default, don't save
          showAllColumns: parsed.showAllColumns || false,
          sortBy: parsed.sortBy || 'RatePerMile'
        };
      }
    } catch (error) {
      console.warn('Error loading filters from localStorage:', error);
    }
    // Return defaults if no saved filters or error
    return {
      minPayment: 500,
      minRatePerMile: 2.50,
      minMiles: 0,
      haulTypes: DEFAULT_HAUL_TYPES,
      loadTypes: DEFAULT_LOAD_TYPES,
      maxLength: 60,
      maxWeight: 50000,
      destinationStates: US_STATES,
      equipmentTypes: DEFAULT_EQUIPMENT_TYPES,
      showAllColumns: false,
      sortBy: 'RatePerMile'
    };
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'RatePerMile',
    direction: 'desc'
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showNotifyEmail, setShowNotifyEmail] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  
  // Live loadboard state
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [newLoads, setNewLoads] = useState([]);
  const [showingNewLoads, setShowingNewLoads] = useState(false);
  const [liveNotification, setLiveNotification] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [selectedCityLoads, setSelectedCityLoads] = useState([]);
  const [displayMode, setDisplayMode] = useState('payment'); // 'payment', 'rate', 'profit', or 'pricediff'
  const [showFilters, setShowFilters] = useState(false); // Show/hide filters section
  const [showDataTable, setShowDataTable] = useState(false); // Show/hide detailed data table section
  const [mapBackground, setMapBackground] = useState(() => isDarkMode ? 'dark' : 'streets'); // 'light', 'satellite', 'dark', 'streets'
  const [clearRoutes, setClearRoutes] = useState(0); // Trigger to clear routes - increment to trigger
  const [showTopLanesOnly, setShowTopLanesOnly] = useState(false); // Toggle to show only top lanes by price difference
  const [topLanesCount, setTopLanesCount] = useState(10); // Number of top lanes to show (default 10)
  const [showCommentary, setShowCommentary] = useState(false); // Toggle to show/hide map commentary and annotations
  const [isMapFullscreen, setIsMapFullscreen] = useState(false); // Toggle fullscreen mode for map
  const [isTransitioning, setIsTransitioning] = useState(false); // Track transition state

  // Handle fullscreen toggle with transition state
  const toggleFullscreen = () => {
    if (isTransitioning) return; // Prevent multiple rapid toggles

    setIsTransitioning(true);
    setIsMapFullscreen(!isMapFullscreen);

    // Clear transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isMapFullscreen && !isTransitioning) {
        toggleFullscreen();
      }
    };

    if (isMapFullscreen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body from scrolling when in fullscreen
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isMapFullscreen, isTransitioning]);

  // Cost per mile with localStorage persistence - moved up to avoid lexical declaration errors
  const [costPerMile, setCostPerMile] = useState(() => {
    try {
      const saved = localStorage.getItem('lotwizard_cost_per_mile');
      return saved ? parseFloat(saved) : 1.75;
    } catch (error) {
      console.warn('Error loading cost per mile from localStorage:', error);
      return 1.75;
    }
  });

  // Daily search limit tracking for Basic users
  const [dailySearchCount, setDailySearchCount] = useState(() => {
    try {
      const today = new Date().toDateString();
      const savedData = localStorage.getItem('lotwizard_daily_searches');
      if (savedData) {
        const data = JSON.parse(savedData);
        // Reset count if it's a new day
        if (data.date === today) {
          return data.count || 0;
        }
      }
      return 0;
    } catch (error) {
      console.warn('Error loading daily search count from localStorage:', error);
      return 0;
    }
  });

  // Sync sortConfig with filters.sortBy
  useEffect(() => {
    if (filters.sortBy !== sortConfig.key) {
      setSortConfig(prev => ({ ...prev, key: filters.sortBy }));
    }
  }, [filters.sortBy, sortConfig.key]);

  // Save cost per mile to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('lotwizard_cost_per_mile', costPerMile.toString());
      console.log('[SearchLoadsUnified] Saved cost per mile to localStorage:', costPerMile);
    } catch (error) {
      console.warn('Error saving cost per mile to localStorage:', error);
    }
  }, [costPerMile]);

  // Update map background when theme changes
  useEffect(() => {
    setMapBackground(isDarkMode ? 'dark' : 'streets');
  }, [isDarkMode]);

  // Save filters to localStorage whenever they change (excluding equipment types)
  useEffect(() => {
    try {
      const filtersToSave = {
        minPayment: filters.minPayment,
        minRatePerMile: filters.minRatePerMile,
        minMiles: filters.minMiles,
        maxLength: filters.maxLength,
        maxWeight: filters.maxWeight,
        haulTypes: filters.haulTypes,
        loadTypes: filters.loadTypes,
        destinationStates: filters.destinationStates,
        showAllColumns: filters.showAllColumns,
        sortBy: filters.sortBy
        // Note: Excluding equipmentTypes from save
      };
      localStorage.setItem('lotwizard_search_filters', JSON.stringify(filtersToSave));
      console.log('[SearchLoadsUnified] Saved filters to localStorage:', filtersToSave);
    } catch (error) {
      console.warn('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  // Save search origin to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('lotwizard_search_origin', searchFilters.origin);
      console.log('[SearchLoadsUnified] Saved search origin to localStorage:', searchFilters.origin);
    } catch (error) {
      console.warn('Error saving search origin to localStorage:', error);
    }
  }, [searchFilters.origin]);

  // Save equipment selection to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('lotwizard_search_equipment', searchFilters.equipment);
      console.log('[SearchLoadsUnified] Saved equipment selection to localStorage:', searchFilters.equipment);
    } catch (error) {
      console.warn('Error saving equipment selection to localStorage:', error);
    }
  }, [searchFilters.equipment]);

  // Daily search limit functions for Basic users
  const checkDailySearchLimit = () => {
    if (userRole !== 'Basic') return true; // No limit for non-Basic users
    
    const DAILY_SEARCH_LIMIT = 5;
    return dailySearchCount < DAILY_SEARCH_LIMIT;
  };

  const incrementDailySearchCount = () => {
    if (userRole !== 'Basic') return; // Only track for Basic users
    
    const today = new Date().toDateString();
    const newCount = dailySearchCount + 1;
    
    try {
      localStorage.setItem('lotwizard_daily_searches', JSON.stringify({
        date: today,
        count: newCount
      }));
      setDailySearchCount(newCount);
      console.log('[SearchLoadsUnified] Daily search count updated:', newCount);
    } catch (error) {
      console.warn('Error saving daily search count to localStorage:', error);
    }
  };

  const getRemainingSearches = () => {
    if (userRole !== 'Basic') return null; // Unlimited for non-Basic users
    
    const DAILY_SEARCH_LIMIT = 5;
    return Math.max(0, DAILY_SEARCH_LIMIT - dailySearchCount);
  };

  // Function to log search following the Python pattern
  const logSearchActivity = async (searchParameters) => {
    try {
      const accessToken = await getAccessTokenSilently();
      
      // Get the tai-khoan API URL - this is where user functions are handled
      const isLocal = window.location.hostname === 'localhost';
      const apiUrl = isLocal 
        ? 'http://64.225.20.106:8081'  // Development - direct connection to tai-khoan container
        : '/tai-khoan';  // Production - nginx proxy to tai-khoan-container
      
      // Prepare the payload following the Python add_search_log function pattern
      const payload = {
        user_id: userData?.id || user?.sub || 'unknown',
        user_sub: user?.sub || userData?.user_sub || 'unknown',
        function_page: 'Search Loads',
        search_parameters: JSON.stringify({
          origin: searchParameters.origin,
          pickup_date: searchParameters.pickupDate,
          equipment: searchParameters.equipment,
          deadhead_miles: searchParameters.originRange
        })
      };

      console.log('[SearchLoadsUnified] Logging search activity with payload:', payload);

      // Make the API call to /add_search endpoint
      const response = await fetch(`${apiUrl}/add_search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[SearchLoadsUnified] Search activity logged successfully:', result);
        return result;
      } else {
        const errorText = await response.text();
        console.warn('[SearchLoadsUnified] Failed to log search activity:', response.status, errorText);
      }
    } catch (error) {
      console.error('[SearchLoadsUnified] Error logging search activity:', error);
    }
  };

  // Handle origin input change with Mapbox suggestions
  const handleOriginChange = async (e) => {
    const value = e.target.value;
    console.log('📝 Origin input changed:', value);
    setSearchFilters(prev => ({ ...prev, origin: value }));
    
    if (value.length >= 2) {
      console.log('📝 Starting suggestion fetch...');
      setLoadingSuggestions(true);
      try {
        // Get suggestions from Mapbox Geocoding API
        const suggestions = await getMapboxCitySuggestions(value);
        console.log('📝 Got suggestions:', suggestions);
        setFilteredSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
        console.log('📝 Set showSuggestions to:', suggestions.length > 0);
      } catch (error) {
        console.warn('📝 Error getting city suggestions:', error);
        setFilteredSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
        console.log('📝 Loading finished');
      }
    } else {
      console.log('📝 Query too short, hiding suggestions');
      setShowSuggestions(false);
      setFilteredSuggestions([]);
      setLoadingSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    console.log('🎯 Selecting suggestion:', suggestion);
    setSearchFilters(prev => ({ ...prev, origin: suggestion }));
    setShowSuggestions(false);
    setFilteredSuggestions([]);
  };

  // Handle input focus - trigger suggestions if there's text
  const handleOriginFocus = async () => {
    if (searchFilters.origin.length >= 2) {
      setLoadingSuggestions(true);
      try {
        const suggestions = await getMapboxCitySuggestions(searchFilters.origin);
        setFilteredSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.warn('Error getting city suggestions on focus:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    }
  };

  // Handle input blur (with slight delay to allow clicking suggestions)
  const handleOriginBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Testing function to verify new filters
  const testNewFilters = () => {
    console.log('🧪 Testing New Filters Implementation...');
    
    try {
      // Test 1: Validate filter state
      console.log('✅ Test 1 - Filter State:', {
        minMiles: filters.minMiles,
        loadTypes: filters.loadTypes,
        haulTypes: filters.haulTypes
      });
      
      // Test 2: Test buildSearchParams with new filters
      const testParams = buildSearchParams();
      console.log('✅ Test 2 - API Parameters:', {
        min_miles: testParams.min_miles,
        load_types: testParams.load_types
      });
      
      // Test 3: Test filtering with mock data
      const mockData = [
        { ID: 1, PaymentAmount: 1000, RatePerMile: 2.5, Mileage: 300, Equipment: 'F', DestinationState: 'IL', Length: 50, Weight: 25000, HaulType: 'Medium Haul', LoadType: 'Full' },
        { ID: 2, PaymentAmount: 800, RatePerMile: 2.0, Mileage: 100, Equipment: 'V', DestinationState: 'CA', Length: 30, Weight: 15000, HaulType: 'Short Haul', LoadType: 'Partial' }
      ];
      
      const filteredData = applyFilters(mockData);
      console.log('✅ Test 3 - Filter Results:', {
        originalCount: mockData.length,
        filteredCount: filteredData.length,
        filteredData
      });
      
      // Test 4: Test error handling
      const invalidData = [
        { ID: 3, PaymentAmount: null, Mileage: 'invalid' }, // Invalid data
        { ID: 4, PaymentAmount: 1200, RatePerMile: 3.0, Mileage: 500 } // Missing fields
      ];
      
      const errorFilteredData = applyFilters(invalidData);
      console.log('✅ Test 4 - Error Handling:', {
        invalidOriginalCount: invalidData.length,
        errorFilteredCount: errorFilteredData.length
      });
      
      console.log('🎉 All filter tests completed successfully!');
      return true;
      
    } catch (error) {
      console.error('❌ Filter testing failed:', error);
      return false;
    }
  };

  // Auto-run tests when component mounts (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🧪 Running Filter Tests automatically...');
      setTimeout(testNewFilters, 1000); // Small delay to ensure component is fully loaded
    }
  }, []);

  // Live polling effect - runs every 10 seconds when live mode is enabled
  // TEMPORARILY DISABLED - FIXING STRUCTURE ISSUES
  // useEffect(() => {
  //   if (!isLiveMode) return;
  //   const interval = setInterval(performLiveUpdate, 10000);
  //   console.log('[LiveLoadboard] Live polling started (every 10 seconds)');
  //   return () => {
  //     clearInterval(interval);
  //     console.log('[LiveLoadboard] Live polling stopped');
  //   };
  // }, [isLiveMode, lastUpdateTime, newLoads.length]);

  // Aggregate data by destination city for bubble map
  const aggregateByDestinationCity = (data) => {
    const cityData = {};
    
    data.forEach(load => {
      const cityKey = `${load.DestinationCity}, ${load.DestinationState}`;
      
      if (!cityData[cityKey]) {
        cityData[cityKey] = {
          city: load.DestinationCity,
          state: load.DestinationState,
          cityState: cityKey,
          loads: [],
          totalPayment: 0,
          avgPayment: 0,
          avgRate: 0,
          avgDistance: 0,
          loadCount: 0,
          // Use destination coordinates for bubble placement
          lat: parseFloat(load.dLat),
          lon: parseFloat(load.dLon)
        };
      }
      
      cityData[cityKey].loads.push(load);
      cityData[cityKey].totalPayment += load.PaymentAmount;
      cityData[cityKey].loadCount++;
    });
    
    // Calculate averages
    Object.values(cityData).forEach(city => {
      city.avgPayment = city.totalPayment / city.loadCount;
      city.avgRate = city.loads.reduce((sum, load) => sum + load.RatePerMile, 0) / city.loadCount;
      city.avgDistance = city.loads.reduce((sum, load) => sum + load.Mileage, 0) / city.loadCount;

      // Calculate average profit as Payment - (Mileage × Cost per Mile)
      city.totalProfit = city.loads.reduce((sum, load) => {
        return sum + (load.PaymentAmount - (load.Mileage * costPerMile));
      }, 0);
      city.avgProfit = city.totalProfit / city.loadCount;

      // Calculate average price difference (for predicted pricing)
      city.avgPriceDiff = city.loads.reduce((sum, load) => {
        const priceDiff = parseFloat(load.PriceDifference) || 0;
        return sum + priceDiff;
      }, 0) / city.loadCount;
    });
    
    return Object.values(cityData);
  };

  // Get filtered city data based on top lanes toggle
  const getFilteredCityData = (data) => {
    const cityData = aggregateByDestinationCity(data);

    if (!showTopLanesOnly) {
      return cityData;
    }

    // Filter to show only top lanes by price difference (highest first)
    return cityData
      .filter(city => city.avgPriceDiff > 0) // Only show lanes with positive price difference
      .sort((a, b) => b.avgPriceDiff - a.avgPriceDiff) // Sort by highest price difference first
      .slice(0, topLanesCount); // Take only the top N lanes
  };

  // Get the best rated lane (highest price difference) for commentary
  const getBestRatedLane = (data) => {
    const cityData = aggregateByDestinationCity(data);

    // Find lane with highest positive price difference
    const validLanes = cityData.filter(city => city.avgPriceDiff > 0);
    if (validLanes.length === 0) return null;

    return validLanes.reduce((best, current) =>
      current.avgPriceDiff > best.avgPriceDiff ? current : best
    );
  };

  // Format payment amounts for display
  const formatPaymentForBubble = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
    return `$${Math.round(amount)}`;
  };

  // Get color based on payment amount (REVERSED: Green for high, red for low)
  const getPaymentColor = (amount) => {
    if (amount >= 3000) return '#16a34a'; // Green - High value
    if (amount >= 2000) return '#65a30d'; // Green-yellow
    if (amount >= 1500) return '#ca8a04'; // Yellow-orange
    if (amount >= 1000) return '#d97706'; // Orange
    if (amount >= 500) return '#ea580c';  // Orange-red
    return '#dc2626'; // Red - Lower value
  };

  // Handle city bubble click
  const handleCityClick = (cityData) => {
    setSelectedCity(cityData.cityState);
    setSelectedCityLoads(cityData.loads);
    console.log('🎯 Selected city:', cityData.cityState, 'with', cityData.loadCount, 'loads');
    
    // Log city click activity
    try {
      logCityClick({
        cityName: cityData.cityState,
        cityState: cityData.cityState,
        loadCount: cityData.loadCount,
        avgPayment: cityData.avgPayment,
        avgRate: cityData.avgRate,
        avgPriceDiff: cityData.avgPriceDiff,
        bubbleSize: cityData.bubbleSize
      });
    } catch (logError) {
      console.warn('[SearchLoadsUnified] Failed to log city click:', logError);
    }
  };

  // Set up global function for popup "View All Loads" button
  useEffect(() => {
    window.showAllLoads = (cityState) => {
      const cityData = aggregateByDestinationCity(results).find(city => city.cityState === cityState);
      if (cityData) {
        handleCityClick(cityData);
        // Scroll to the data table section
        setTimeout(() => {
          const tableSection = document.querySelector('[data-section="data-table"]');
          if (tableSection) {
            tableSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    return () => {
      // Cleanup global function
      if (window.showAllLoads) {
        delete window.showAllLoads;
      }
    };
  }, [results]);

  // Handle setting new origin from map right-click
  const handleSetOrigin = async (newOrigin) => {
    console.log('🗺️ handleSetOrigin called with:', newOrigin);
    console.log('🗺️ Current searchFilters:', searchFilters);
    
    // Update the search filters
    setSearchFilters(prev => ({ ...prev, origin: newOrigin }));
    
    // Automatically trigger a new search with the new origin
    setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [originCity, originState] = newOrigin.split(', ');
        if (!originState) {
          // If no state found, treat as coordinates or partial city name
          console.log('🎯 New origin might be coordinates or partial city:', newOrigin);
        }

        const searchParams = {
          OriginCity: originCity || newOrigin,
          OriginState: originState || 'IL', // Default to IL for coordinates
          OriginRange: searchFilters.originRange.toString(),
          PickupDate: searchFilters.pickupDate,
          cost_per_mile: costPerMile
        };
        
        console.log('[SearchLoadsUnified] Auto-searching with new origin:', searchParams);
        
        let searchResults;
        
        try {
          const response = await apiManager.searchLoads(searchParams);
          searchResults = response.data;
          console.log('[SearchLoadsUnified] Auto-search successful! Found:', searchResults.length, 'loads');
          setResults(searchResults);
          setError(null);
        } catch (apiError) {
          console.warn('[SearchLoadsUnified] Auto-search failed, using mock data:', apiError.message);
          searchResults = generateMockResults(searchParams);
          setResults(searchResults);
          setError(`API unavailable: ${apiError.message}. Showing sample data for demonstration.`);
        }
        
      } catch (err) {
        console.error('[SearchLoadsUnified] Auto-search error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 100); // Small delay to ensure state update
  };

  // Build search parameters for API call with error handling
  const buildSearchParams = () => {
    try {
      // Safely parse origin with fallbacks
      const originParts = searchFilters.origin ? searchFilters.origin.split(', ') : ['', ''];
      const originCity = originParts[0] || '';
      const originState = originParts[1] || '';
      
      // Validate required parameters
      if (!originCity || !originState) {
        throw new Error('Origin city and state are required');
      }
      
      // Get selected equipment types
      const selectedEquipmentTypes = Array.isArray(filters.equipmentTypes) ? filters.equipmentTypes.filter(Boolean) : [];

      // Core search parameters only - filters will be applied client-side after API returns data
      const params = {
        OriginCity: originCity.trim(),
        OriginState: originState.trim(),
        OriginRange: Math.max(1, parseInt(searchFilters.originRange) || 100).toString(),
        PickupDate: searchFilters.pickupDate || new Date().toISOString().split('T')[0],
        // TODO: Add deadhead parameter when available
        limit: 1000 // Increase result limit from default (likely 250) to 1000
      };

      // Equipment type filtering strategy:
      // - Single equipment type: Use server-side filtering (equipment_type parameter)
      // - Multiple equipment types: Use client-side filtering after API response
      if (selectedEquipmentTypes.length === 1) {
        params.equipment_type = selectedEquipmentTypes[0];
        console.log('[SearchLoadsUnified] Using server-side equipment filtering:', params.equipment_type);
      } else if (selectedEquipmentTypes.length > 1) {
        console.log('[SearchLoadsUnified] Will apply client-side equipment filtering for:', selectedEquipmentTypes);
      }
      // If no equipment types selected, search all equipment types

      console.log('[SearchLoadsUnified] Core API search parameters (filters applied client-side):', params);
      
      console.log('[SearchLoadsUnified] Built search parameters:', params);
      return params;
    } catch (error) {
      console.error('[SearchLoadsUnified] Error building search parameters:', error);
      throw new Error(`Invalid search parameters: ${error.message}`);
    }
  };

  // Fallback mock data generator (unchanged)
  const generateMockResults = (searchParams) => {
    const mockResults = [];
    const origins = [
      { city: 'Chicago, IL', lat: 41.8781, lon: -87.6298 },
      { city: 'Detroit, MI', lat: 42.3314, lon: -83.0458 },
      { city: 'Milwaukee, WI', lat: 43.0389, lon: -87.9065 },
      { city: 'Indianapolis, IN', lat: 39.7684, lon: -86.1581 }
    ];
    const destinations = [
      { city: 'Los Angeles, CA', lat: 34.0522, lon: -118.2437 },
      { city: 'Phoenix, AZ', lat: 33.4484, lon: -112.0740 },
      { city: 'Denver, CO', lat: 39.7392, lon: -104.9903 },
      { city: 'Dallas, TX', lat: 32.7767, lon: -96.7970 },
      { city: 'Atlanta, GA', lat: 33.7490, lon: -84.3880 }
    ];
    const equipmentTypes = ['F', 'FA', 'FSD', 'V', 'R', 'HS'];
    const brokers = ['ABC Logistics', 'FreightCorp', 'LoadMaster', 'TransPort Inc', 'ShipRight'];
    
    for (let i = 0; i < 25; i++) {
      const distance = 200 + Math.random() * 1500;
      const baseRate = 2.20 + Math.random() * 1.8;
      const payment = Math.round(distance * baseRate);
      const origin = origins[Math.floor(Math.random() * origins.length)];
      const destination = destinations[Math.floor(Math.random() * destinations.length)];
      
      mockResults.push({
        ID: `LOAD${1000 + i}`,
        PickupDate: searchParams.PickupDate,
        PaymentAmount: payment,
        RatePerMile: Math.round(baseRate * 100) / 100,
        RatePerMileWithDH: Math.round((baseRate + 0.1) * 100) / 100,
        OperatingProfit: Math.round(payment * 0.15),
        Origin: origin.city,
        Destination: destination.city,
        oLat: origin.lat,
        oLon: origin.lon,
        dLat: destination.lat,
        dLon: destination.lon,
        OriginCity: origin.city.split(', ')[0],
        OriginState: origin.city.split(', ')[1],
        DestinationCity: destination.city.split(', ')[0],
        OriginDistance: Math.floor(Math.random() * 150) + 10,
        Mileage: Math.round(distance),
        Equipment: equipmentTypes[Math.floor(Math.random() * equipmentTypes.length)],
        Length: Math.floor(Math.random() * 53) + 20,
        Weight: Math.floor(Math.random() * 40000) + 10000,
        TruckCompanyName: brokers[Math.floor(Math.random() * brokers.length)],
        PointOfContact: `Contact ${i + 1}`,
        PointOfContactPhone: `555-${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
        BrokerEmail: `broker${i + 1}@freight.com`,
        MCNumber: `MC${100000 + Math.floor(Math.random() * 900000)}`,
        SpecInfo: Math.random() > 0.7 ? 'Hazmat' : 'Standard',
        LoadType: LOAD_TYPES[Math.floor(Math.random() * LOAD_TYPES.length)],
        BrokerCity: 'Chicago',
        BrokerState: 'IL',
        BrokerID: `B${1000 + i}`,
        HaulType: HAUL_TYPES[Math.floor(Math.random() * HAUL_TYPES.length)],
        DestinationState: destination.city.split(', ')[1]
      });
    }
    
    return mockResults;
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    // Check daily search limit for Basic users
    if (!checkDailySearchLimit()) {
      const remaining = getRemainingSearches();
      setError(`Daily search limit reached. Basic users are limited to 5 searches per day. You have ${remaining} searches remaining today. Upgrade to a paid plan for unlimited searches.`);
      return;
    }

    // Clear any existing driving directions before new search
    setSelectedCity(null); // This will clear the selected city results table too
    setClearRoutes(prev => prev + 1); // Trigger route clearing in map component

    setLoading(true);
    setError(null);
    
    try {
      // Parse origin city and state
      const [originCity, originState] = searchFilters.origin.split(', ');
      if (!originState) {
        throw new Error('Please enter city and state in format: "City, ST"');
      }

      // Build search parameters for API
      const searchParams = buildSearchParams();
      console.log('[SearchLoadsUnified] Searching with parameters:', searchParams);
      
      let searchResults;
      let usingMockData = false;
      const searchStartTime = performance.now();
      
      try {
        // Use unified API manager
        console.log('[SearchLoadsUnified] Attempting unified API call...');
        const response = await apiManager.searchLoads(searchParams);
        searchResults = response.data;
        console.log('[SearchLoadsUnified] Unified API Success! Returned:', searchResults.length, 'loads');

        // Debug: Check the first result to see modeled pricing fields
        if (searchResults.length > 0) {
          console.log('[SearchLoadsUnified] First result sample:', searchResults[0]);
          console.log('[SearchLoadsUnified] Modeled pricing fields check:', {
            modeled_price: searchResults[0].modeled_price,
            modeled_rate_per_mile: searchResults[0].modeled_rate_per_mile,
            price_difference: searchResults[0].price_difference,
            percentage_difference: searchResults[0].percentage_difference,
            pricing_status: searchResults[0].pricing_status,
            has_model_prediction: searchResults[0].has_model_prediction
          });
        }
      } catch (apiError) {
        console.warn('[SearchLoadsUnified] Unified API call failed, falling back to mock data:', apiError.message);
        
        // Fallback to mock data if API is not available
        searchResults = generateMockResults(searchParams);
        usingMockData = true;
        
        // Show a warning but don't fail the search
        setError(`API unavailable: ${apiError.message}. Showing sample data for demonstration.`);
      }
      
      // Process and store results
      setResults(searchResults);
      
      // Increment daily search count for Basic users (counts both API and mock data)
      incrementDailySearchCount();
      
      if (!usingMockData) {
        // Clear any previous API errors if the call succeeded
        setError(null);
      }
      
      // Log search activity using the correct API endpoint
      try {
        await logSearchActivity({
          origin: searchFilters.origin,
          pickupDate: searchFilters.pickupDate,
          equipment: searchFilters.equipment,
          originRange: searchFilters.originRange
        });
      } catch (logError) {
        console.warn('[SearchLoadsUnified] Failed to log search activity:', logError);
      }
      
    } catch (err) {
      console.error('[SearchLoadsUnified] Search error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Live polling function for new loads
  const performLiveUpdate = async () => {
    if (!isLiveMode) return;

    try {
      console.log('[LiveLoadboard] Performing background update...');
      
      // Build search parameters using current filters
      const searchParams = buildSearchParams();
      
      // Add timestamp filter to only get loads newer than last update
      if (lastUpdateTime) {
        searchParams.since = lastUpdateTime;
      }
      
      // Use unified API manager for live updates
      const response = await apiManager.searchLoads(searchParams);
      const latestLoads = response.data;
      
      if (latestLoads.length > 0) {
        console.log(`[LiveLoadboard] Found ${latestLoads.length} new loads`);
        
        // Add to new loads accumulator
        setNewLoads(prev => {
          const loadMap = new Map();
          // Add existing new loads
          prev.forEach(load => loadMap.set(load.Load_ID, load));
          // Add latest loads (overwrites duplicates)
          latestLoads.forEach(load => loadMap.set(load.Load_ID, load));
          return Array.from(loadMap.values());
        });
        
        // Update last update time
        setLastUpdateTime(new Date().toISOString());
        
        // Check if we should show notification (every 5 minutes or 10+ new loads)
        const timeSinceLastNotification = Date.now() - (liveNotification?.timestamp || 0);
        const shouldNotify = newLoads.length >= 10 || timeSinceLastNotification > 300000; // 5 minutes
        
        if (shouldNotify && !showingNewLoads) {
          setLiveNotification({
            count: newLoads.length + latestLoads.length,
            timestamp: Date.now(),
            show: true
          });
        }
      }
      
    } catch (error) {
      console.warn('[LiveLoadboard] Live update failed:', error);
      // Don't show error to user for background updates
    }
  };

  // Handle viewing new loads
  const handleViewNewLoads = () => {
    console.log(`[LiveLoadboard] Viewing ${newLoads.length} new loads`);
    
    // Switch to showing new loads mode
    setShowingNewLoads(true);
    
    // Update results to show new loads + existing (existing will be grayed)
    const combinedResults = [...results, ...newLoads];
    setResults(combinedResults);
    
    // Hide notification
    setLiveNotification(prev => ({ ...prev, show: false }));
    
    // The BubbleMapResults component will handle highlighting new loads
  };

  // Handle showing all loads (exit new loads view)
  const handleShowAllLoads = () => {
    setShowingNewLoads(false);
    setNewLoads([]); // Clear new loads accumulator
    setLiveNotification(null);
  };

  // Toggle live mode
  const handleToggleLiveMode = () => {
    setIsLiveMode(prev => {
      const newValue = !prev;
      console.log(`[LiveLoadboard] Live mode ${newValue ? 'enabled' : 'disabled'}`);
      
      if (newValue) {
        // Starting live mode
        setLastUpdateTime(new Date().toISOString());
        setNewLoads([]);
        setLiveNotification(null);
      } else {
        // Stopping live mode
        setNewLoads([]);
        setLiveNotification(null);
        setShowingNewLoads(false);
      }
      
      return newValue;
    });
  };

  // Apply filters with error handling for missing/invalid data
  const applyFilters = (data) => {
    if (!Array.isArray(data)) {
      console.warn('[SearchLoadsUnified] Invalid data array passed to applyFilters:', data);
      return [];
    }

    return data.filter(load => {
      try {
        // Safely check each filter with fallbacks for missing data
        const paymentAmount = parseFloat(load.PaymentAmount) || 0;
        const ratePerMile = parseFloat(load.RatePerMile) || 0;
        const mileage = parseFloat(load.Mileage) || 0;
        const length = parseFloat(load.Length) || 0;
        const weight = parseFloat(load.Weight) || 0;
        const equipment = load.Equipment || '';
        const destinationState = load.DestinationState || '';
        const haulType = load.HaulType || '';
        const loadType = load.LoadType || '';

        return (
          paymentAmount >= filters.minPayment &&
          ratePerMile >= filters.minRatePerMile &&
          mileage >= filters.minMiles &&
          filters.equipmentTypes.includes(equipment) &&
          filters.destinationStates.includes(destinationState) &&
          length <= filters.maxLength &&
          weight <= filters.maxWeight &&
          filters.haulTypes.includes(haulType) &&
          filters.loadTypes.includes(loadType)
        );
      } catch (error) {
        console.warn('[SearchLoadsUnified] Error filtering load:', load, error);
        return false; // Exclude loads that cause filtering errors
      }
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    // Log table sort activity
    try {
      logTableSort('search_loads', key, direction);
    } catch (logError) {
      console.warn('[SearchLoadsUnified] Failed to log table sort:', logError);
    }
    
    setSortConfig({ key, direction });
    setFilters(prev => ({ ...prev, sortBy: key }));
  };

  const getSortValue = (item, key) => {
    const value = item[key];
    if (typeof value === 'string') return value.toLowerCase();
    if (typeof value === 'number') return value;
    return 0;
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-primary-600" />
      : <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  const filteredResults = applyFilters(results);
  const sortedResults = [...filteredResults].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });


  // Other helper functions remain the same...
  const selectAllEquipment = () => {
    setFilters(prev => ({ ...prev, equipmentTypes: EQUIPMENT_TYPES }));
  };

  const clearAllEquipment = () => {
    setFilters(prev => ({ ...prev, equipmentTypes: [] }));
  };

  const selectAllStates = () => {
    setFilters(prev => ({ ...prev, destinationStates: US_STATES }));
  };

  const clearAllStates = () => {
    setFilters(prev => ({ ...prev, destinationStates: [] }));
  };

  const toggleRowSelection = (loadId) => {
    setSelectedRows(prev => 
      prev.includes(loadId) 
        ? prev.filter(id => id !== loadId)
        : [...prev, loadId]
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const displayColumns = filters.showAllColumns
    ? ['ID', 'PickupDate', 'Origin', 'Destination', 'Mileage', 'PaymentAmount', 'PredictedPrice', 'PriceDifference', 'RatePerMile', 'Equipment', 'Length', 'Weight', 'TruckCompanyName', 'PointOfContact', 'PointOfContactPhone']
    : ['ID', 'PickupDate', 'Origin', 'Destination', 'Mileage', 'PaymentAmount', 'PredictedPrice', 'PriceDifference', 'RatePerMile', 'Equipment', 'TruckCompanyName'];

  return (
    <div className="space-y-8">{/* Main Container */}
      {/* Basic Plan Notice */}
      {userRole === 'Basic' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-xs">ℹ</span>
            </div>
            <div>
              <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">Basic Plan - Limited Daily Searches</p>
              <p className="text-blue-600 dark:text-blue-400 text-sm">
                You have {getRemainingSearches()} of 5 daily searches remaining. Upgrade to a paid plan for unlimited load searches.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Compact Search Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <form onSubmit={handleSearchSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* Origin with Autocomplete */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Origin
              </label>
              <input
                type="text"
                value={searchFilters.origin}
                onChange={handleOriginChange}
                onFocus={handleOriginFocus}
                onBlur={handleOriginBlur}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Chicago, IL"
                autoComplete="off"
                required
              />
              
              {/* City Suggestions Dropdown */}
              {(showSuggestions || loadingSuggestions) && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {loadingSuggestions ? (
                    <div className="px-3 py-2 text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span>Loading cities...</span>
                    </div>
                  ) : (
                    filteredSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur from firing
                          handleSuggestionSelect(suggestion);
                        }}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <span>{suggestion}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Pickup Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Pickup Date
              </label>
              <input
                type="date"
                value={searchFilters.pickupDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, pickupDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            {/* Equipment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Equipment
              </label>
              <select
                value={searchFilters.equipment}
                onChange={(e) => {
                  const selectedEquipment = e.target.value;
                  setSearchFilters(prev => ({ ...prev, equipment: selectedEquipment }));
                  
                  // Update equipment filters based on selection
                  if (selectedEquipment === 'All') {
                    // Reset to default equipment types when "All" is selected
                    setFilters(prev => ({ ...prev, equipmentTypes: DEFAULT_EQUIPMENT_TYPES }));
                  } else {
                    // Set equipment filter to only the selected type
                    setFilters(prev => ({ ...prev, equipmentTypes: [selectedEquipment] }));
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {SEARCH_EQUIPMENT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Deadhead Distance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Deadhead: {searchFilters.originRange} mi
              </label>
              <input
                type="range"
                min="25"
                max="300"
                step="25"
                value={searchFilters.originRange}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, originRange: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            {/* Search Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Live Loadboard Controls - Hidden for now */}
      {/* {results.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <button
                  disabled={true}
                  className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50"
                  title="Live Loadboard temporarily disabled for maintenance"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isLiveMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <div className="ml-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white dark:text-white">
                      Live Loadboard
                    </span>
                    <span className="ml-2 text-xs text-amber-600 dark:text-amber-400 italic">
                      (Temporarily disabled for maintenance)
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-400">
                    Feature will be available soon
                  </p>
                </div>
              </div>
            </div>

            <div className="text-right">
              {isLiveMode && lastUpdateTime && (
                <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-400">
                  Last checked: {new Date(lastUpdateTime).toLocaleTimeString()}
                </div>
              )}
              {newLoads.length > 0 && !liveNotification?.show && (
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  {newLoads.length} new loads pending notification
                </div>
              )}
            </div>
          </div>

          {showingNewLoads && (
            <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    Showing {newLoads.length} new loads (existing loads are grayed out)
                  </span>
                </div>
                <button
                  onClick={handleShowAllLoads}
                  className="px-3 py-1 text-xs bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Show All Loads
                </button>
              </div>
            </div>
          )}
        </div>
      )} */}

      {/* Live Notification */}
      {liveNotification?.show && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white rounded-lg shadow-lg p-4 max-w-sm animate-bounce">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                🔴
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">New Loads Available!</h3>
              <p className="text-sm opacity-90">
                {liveNotification.count} new loads found
              </p>
            </div>
          </div>
          <div className="mt-3 flex space-x-2">
            <button
              onClick={handleViewNewLoads}
              className="flex-1 bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm font-medium transition-colors"
            >
              View New Loads
            </button>
            <button
              onClick={() => setLiveNotification(prev => ({ ...prev, show: false }))}
              className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Search Map immediately below search parameters */}
      {results.length > 0 && (
        <div className={`${isMapFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900' : 'mt-6'}`}>
          {/* Fullscreen Close Button */}
          {isMapFullscreen && (
            <button
              onClick={toggleFullscreen}
              disabled={isTransitioning}
              className={`absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 rounded-lg p-2 shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                isTransitioning ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Exit fullscreen (or press Escape)"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          <div
            className={`${isMapFullscreen ? 'h-full' : 'h-[37.5rem]'} rounded-lg overflow-hidden border border-gray-200`}
            style={{ transition: 'none' }}
          >
            <BubbleMapResults
              cityData={getFilteredCityData(sortedResults)}
              isVisible={true}
              searchOrigin={searchFilters.origin}
              onCityClick={handleCityClick}
              onSetOrigin={handleSetOrigin}
              displayMode={displayMode}
              onDisplayModeChange={setDisplayMode}
              newLoads={newLoads}
              showingNewLoads={showingNewLoads}
              mapBackground={mapBackground}
              formatPaymentForBubble={formatPaymentForBubble}
              getPaymentColor={getPaymentColor}
              formatNumber={formatNumber}
              formatCurrency={formatCurrency}
              clearRoutes={clearRoutes}
              showCommentary={showCommentary}
              bestRatedLane={getBestRatedLane(sortedResults)}
              isMapFullscreen={isMapFullscreen}
            />
          </div>
        </div>
      )}

      {/* Map Controls - Shows below map, above filters */}
      {results.length > 0 && (
        <div className="mt-4 flex items-center space-x-4 overflow-x-auto scrollbar-hide flex-nowrap justify-start sm:justify-center px-4 sm:px-0 -mx-4 sm:mx-0">
          {/* Map Background Toggle */}
          <div className="flex-shrink-0 flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700">
            <Layers className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
              Background:
            </label>
            <select
              value={mapBackground}
              onChange={(e) => setMapBackground(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              <option value="light">Light</option>
              <option value="satellite">Satellite</option>
              <option value="dark">Dark</option>
              <option value="streets">Streets</option>
            </select>
          </div>

          {/* Display Mode Toggle */}
          <div className="flex-shrink-0 flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700">
            <BarChart3 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
              Display:
            </label>
            <div className="flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setDisplayMode('payment')}
                className={`px-2 py-1 text-xs font-medium transition-colors ${
                  displayMode === 'payment'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Show payment amounts"
              >
                💰 Payment
              </button>
              <button
                onClick={() => setDisplayMode('rate')}
                className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  displayMode === 'rate'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Show rate per mile"
              >
                📈 Rate/mi
              </button>
              <button
                onClick={() => setDisplayMode('profit')}
                className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  displayMode === 'profit'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Show estimated profit (requires cost per mile)"
              >
                📊 Profit
              </button>
              <button
                onClick={() => setDisplayMode('pricediff')}
                className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  displayMode === 'pricediff'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Show price difference from predicted value"
              >
                🎯 Price Diff
              </button>
            </div>
          </div>
          
          {/* Cost Per Mile Input - Shows only when Profit display mode is selected */}
          {displayMode === 'profit' && (
            <div className="flex-shrink-0 flex items-center space-x-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Cost/mi:
                <span className="ml-1 text-xs text-gray-600 dark:text-gray-400">(saved)</span>
              </label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-600 dark:text-gray-400">$</span>
                <input
                  type="text"
                  value={costPerMile}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Allow only numbers, decimal point, and common decimal patterns
                    // Allows: "", "1", "1.", ".5", "1.5", etc.
                    if (inputValue === '' || /^(\d+\.?\d*|\.\d*)$/.test(inputValue)) {
                      setCostPerMile(inputValue);
                    }
                  }}
                  onBlur={(e) => {
                    // Ensure we have a valid number when input loses focus
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 0 || e.target.value === '' || e.target.value === '.') {
                      setCostPerMile(1.75); // Reset to default if invalid or empty
                    } else {
                      setCostPerMile(value); // Convert valid string to number
                    }
                  }}
                  className="pl-5 pr-2 py-1 w-20 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                  placeholder="1.75"
                  title={`Current cost per mile: $${typeof costPerMile === 'number' ? costPerMile.toFixed(2) : costPerMile}`}
                />
              </div>
            </div>
          )}

          {/* Top Lanes Filter */}
          <div className="flex-shrink-0 flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700">
            <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Show:
            </label>
            <div className="flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setShowTopLanesOnly(false)}
                className={`px-2 py-1 text-xs font-medium transition-colors ${
                  !showTopLanesOnly
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Show all destination lanes"
              >
                All Lanes
              </button>
              <button
                onClick={() => setShowTopLanesOnly(true)}
                className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  showTopLanesOnly
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title={`Show top ${topLanesCount} lanes with highest price differences`}
              >
                Top {topLanesCount} Lanes
              </button>
            </div>
          </div>

          {/* Commentary Toggle */}
          <div className="flex-shrink-0 flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700">
            <MessageSquare className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Commentary:
            </label>
            <div className="flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setShowCommentary(false)}
                className={`px-2 py-1 text-xs font-medium transition-colors ${
                  !showCommentary
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Hide map annotations and commentary"
              >
                Off
              </button>
              <button
                onClick={() => setShowCommentary(true)}
                className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  showCommentary
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Show map annotations and highlight best rated lanes"
              >
                On
              </button>
            </div>
          </div>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            disabled={isTransitioning}
            className={`flex-shrink-0 flex items-center space-x-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              isTransitioning ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={isMapFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isMapFullscreen ? (
              <Minimize className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            ) : (
              <Maximize className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            )}
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {isMapFullscreen ? "Exit Full" : "Fullscreen"}
            </span>
          </button>
        </div>
      )}

      {/* Detailed Data Table Section */}
      {results.length > 0 && (
        <div
          data-section="data-table"
          className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Detailed Data Table</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ({formatNumber(sortedResults.length)} loads)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  console.log('[DEBUG] Raw API Results Sample:');
                  console.log('Total results:', results.length);
                  if (results.length > 0) {
                    console.log('First result (all fields):', results[0]);
                    console.log('All field names:', Object.keys(results[0]));
                    console.log('Sample of first 3 results:', results.slice(0, 3));
                  }
                }}
                className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-purple-700 dark:text-purple-300 hover:text-purple-900 dark:hover:text-purple-100 bg-purple-50 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-600 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                title="Log raw API response data to browser console"
              >
                🔍 Debug API
              </button>
              <button
                onClick={() => setShowDataTable(!showDataTable)}
                className="flex items-center space-x-1 px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <span>{showDataTable ? 'Hide Table' : 'Show Table'}</span>
                {showDataTable ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {showDataTable && (
            <div className="p-6 space-y-4">
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      {[
                        'ID', 'PickupDate', 'Origin', 'Destination', 'Mileage', 'PaymentAmount', 'PredictedPrice', 'PriceDifference', 'RatePerMile', 'RatePerMileWithDH', 'OperatingProfit',
                        'OriginDistance', 'Equipment', 'Length', 'Weight',
                        'BrokerName', 'PointOfContact', 'PointOfContactPhone', 'BrokerEmail', 'MCNumber',
                        'SpecInfo', 'LoadType', 'BrokerCity', 'BrokerState', 'HaulType'
                      ].map(column => (
                        <th
                          key={column}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                          onClick={() => handleSort(column)}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{column.replace(/([A-Z])/g, ' $1').trim()}</span>
                            {getSortIcon(column)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                    {sortedResults.map((load) => (
                      <tr
                        key={load.ID}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {[
                          'ID', 'PickupDate', 'Origin', 'Destination', 'Mileage', 'PaymentAmount', 'PredictedPrice', 'PriceDifference', 'RatePerMile', 'RatePerMileWithDH', 'OperatingProfit',
                          'OriginDistance', 'Equipment', 'Length', 'Weight',
                          'BrokerName', 'PointOfContact', 'PointOfContactPhone', 'BrokerEmail', 'MCNumber',
                          'SpecInfo', 'LoadType', 'BrokerCity', 'BrokerState', 'HaulType'
                        ].map(column => (
                          <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {column === 'PaymentAmount' || column === 'OperatingProfit' || column === 'PredictedPrice' || column === 'PriceDifference' ? formatCurrency(load[column]) :
                             column === 'RatePerMile' || column === 'RatePerMileWithDH' ? `$${load[column]?.toFixed(2) || '0.00'}` :
                             column === 'Mileage' || column === 'Length' || column === 'Weight' || column === 'OriginDistance' ? formatNumber(load[column] || 0) :
                             column === 'SpecInfo' ? (
                               <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                 load[column] === 'Hazmat'
                                   ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                   : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                               }`}>
                                 {load[column] || 'Standard'}
                               </span>
                             ) :
                             load[column] || 'N/A'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {sortedResults.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">No loads match your current filters</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters Section */}
      {results.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Filters</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ({formatNumber(results.length - sortedResults.length)} loads filtered out)
              </span>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-1 px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {showFilters && (
            <div className="p-6 space-y-6">
              {/* Payment/Rate Filters and Load Specifications */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Minimum Values */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Minimum Payment: {formatCurrency(filters.minPayment)}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="100"
                      value={filters.minPayment}
                      onChange={(e) => setFilters(prev => ({ ...prev, minPayment: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>$0</span>
                      <span>$5,000</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Truck className="w-4 h-4 inline mr-1" />
                      Minimum Rate per Mile: ${filters.minRatePerMile.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="5.00"
                      step="0.10"
                      value={filters.minRatePerMile}
                      onChange={(e) => setFilters(prev => ({ ...prev, minRatePerMile: parseFloat(e.target.value) }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>$0.00</span>
                      <span>$5.00</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Navigation className="w-4 h-4 inline mr-1" />
                      Minimum Miles: {formatNumber(filters.minMiles)} miles
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2000"
                      step="50"
                      value={filters.minMiles}
                      onChange={(e) => setFilters(prev => ({ ...prev, minMiles: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>0 mi</span>
                      <span>2,000 mi</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Maximum Values */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Ruler className="w-4 h-4 inline mr-1" />
                      Maximum Length: {filters.maxLength} ft
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="80"
                      step="5"
                      value={filters.maxLength}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxLength: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>20 ft</span>
                      <span>80 ft</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Scale className="w-4 h-4 inline mr-1" />
                      Maximum Weight: {formatNumber(filters.maxWeight)} lbs
                    </label>
                    <input
                      type="range"
                      min="5000"
                      max="80000"
                      step="5000"
                      value={filters.maxWeight}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxWeight: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>5,000</span>
                      <span>80,000</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Equipment Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <Package className="w-4 h-4 inline mr-1" />
                  Equipment Types ({filters.equipmentTypes.length} selected)
                  {searchFilters.equipment !== 'All' && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded-full">
                      Filtered by: {SEARCH_EQUIPMENT_OPTIONS.find(opt => opt.value === searchFilters.equipment)?.label}
                    </span>
                  )}
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={selectAllEquipment}
                    className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearAllEquipment}
                    className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-2">
                  {EQUIPMENT_TYPES.map(type => (
                    <label key={type} className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={filters.equipmentTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, equipmentTypes: [...prev.equipmentTypes, type] }));
                          } else {
                            setFilters(prev => ({ ...prev, equipmentTypes: prev.equipmentTypes.filter(t => t !== type) }));
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700 dark:checked:bg-primary-600"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Haul Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <Map className="w-4 h-4 inline mr-1" />
                  Haul Types ({filters.haulTypes.length} selected)
                </label>
                <div className="flex flex-wrap gap-2">
                  {HAUL_TYPES.map(type => (
                    <label key={type} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.haulTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, haulTypes: [...prev.haulTypes, type] }));
                          } else {
                            setFilters(prev => ({ ...prev, haulTypes: prev.haulTypes.filter(t => t !== type) }));
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700 dark:checked:bg-primary-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Load Types (Full/Partial) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <Package className="w-4 h-4 inline mr-1" />
                  Load Types ({filters.loadTypes.length} selected)
                </label>
                <div className="flex flex-wrap gap-2">
                  {LOAD_TYPES.map(type => (
                    <label key={type} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.loadTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, loadTypes: [...prev.loadTypes, type] }));
                          } else {
                            setFilters(prev => ({ ...prev, loadTypes: prev.loadTypes.filter(t => t !== type) }));
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700 dark:checked:bg-primary-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Destination States */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Destination States ({filters.destinationStates.length} selected)
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={selectAllStates}
                    className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearAllStates}
                    className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {US_STATES.map(state => (
                    <label key={state} className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={filters.destinationStates.includes(state)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, destinationStates: [...prev.destinationStates, state] }));
                          } else {
                            setFilters(prev => ({ ...prev, destinationStates: prev.destinationStates.filter(s => s !== state) }));
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700 dark:checked:bg-primary-600"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{state}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}


      {/* Selected City Results - Shows filtered table when city is clicked */}
      {results.length > 0 && selectedCity && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 font-medium flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <span className="text-gray-900 dark:text-white">
                Loads to {selectedCity} ({formatNumber(selectedCityLoads.length)} loads)
              </span>
            </div>
            <button
              onClick={() => setSelectedCity(null)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              title="Clear selection"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Load ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pickup Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Origin</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Destination</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Distance</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Predicted Price</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price Difference</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rate/Mile</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rate/Mile w/ DH</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Operating Profit</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Equipment</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Length</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Weight</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Broker</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">MC Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Spec Info</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {selectedCityLoads.map((load) => (
                  <tr key={load.ID} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{load.ID}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.PickupDate ? new Date(load.PickupDate).toLocaleDateString() : '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.OriginCity || load.Origin}, {load.OriginState}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.DestinationCity || load.Destination}, {load.DestinationState}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatNumber(load.Mileage)} mi</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatCurrency(load.PaymentAmount)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatCurrency(load.PredictedPrice)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatCurrency(load.PriceDifference)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">${load.RatePerMile ? load.RatePerMile.toFixed(2) : '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">${load.RatePerMileWithDH ? load.RatePerMileWithDH.toFixed(2) : '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatCurrency(load.OperatingProfit)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.Equipment || '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.Length ? `${load.Length}'` : '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.Weight ? `${formatNumber(load.Weight)} lbs` : '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.TruckCompanyName || '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.PointOfContact || '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.PointOfContactPhone || '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{load.MCNumber || '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        load.SpecInfo === 'Hazmat' 
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' 
                          : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      }`}>
                        {load.SpecInfo || 'Standard'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary - Shows API source */}
      {results.length > 0 && (
        <div className="text-center py-4 text-sm text-gray-600">
          Total loads fetched: {formatNumber(results.length)} | After filtering: {formatNumber(sortedResults.length)} ({Math.round((sortedResults.length / results.length) * 100)}%)
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Via Unified API Manager</div>
        </div>
      )}

      {/* End Main Container */}
    </div>
  );
}

export default SearchLoadsUnified;